import Layout from '@/components/layout/Layout';
import { FC, useRef, useState } from 'react';
import BookInfo from '@/components/ui/book-info/BookInfo';
import BookBreadcrumbs from '@/components/ui/book-breadcrumbs/BookBreadcrumbs';
import ReviewsList from '@/components/ui/reviews-list/ReviewsList';
import LitresReader from '@/components/ui/litres-reader/LitresReader';
import FormReview from '@/components/ui/form-review/FormReview';
import SimilarBooks from '@/components/ui/similar-books/SimilarBooks';
import TabMenu from '@/components/ui/tab-menu/TabMenu';
// import ScrollCardsList from '@/components/ui/scroll-cards-list/ScrollCardsList';
import { DEV_ABSOLUTE_URL } from '@/helpers/variables';
import { getAuthorFullName } from '@/helpers/helpers';
import { useGetUrlPath } from '@/hooks/useGetUrlPath';
import { IBookDetailData, IBookFiles } from '@/interfaces/bookDetail.interface';
import { IReviews } from '@/interfaces/reviews.interface';
import { IMenuData } from '@/interfaces/menu.interface';
import styles from './BookDetail.module.scss';

interface IBookDetailPage extends IMenuData {
  book: IBookDetailData;
  reviews: IReviews;
}

interface IDownloadFragment {
  url: string;
  name: string;
  animation: boolean;
}

const BookDetail: FC<IBookDetailPage> = ({ book, reviews, menuGenres, menuNew }) => {
  const bookData = book.book;
  const otherAuthorBooks = book.other_author_books;
  const { first_name, middle_name, last_name } = book.author;

  const authorFullName = getAuthorFullName(first_name, middle_name, last_name);

  const SEO_TITLE = `${book.book.title} (${authorFullName}) – скачать в fb2, epub, pdf на Fenzin`;
  const SEO_DESCRIPTION = `${book.book.title} (${authorFullName}) – аннотация, отзывы, информация о книге. Рецензии. Переходите и скачивайте книгу / читайте онлайн на Fenzin!`;
  const SEO_CANONICAL = useGetUrlPath();
  const SEO_COVER = `https://kuchaknig.org/covers/${bookData.external_id}.jpg`;

  const refReviewForm = useRef<HTMLDivElement>(null);

  const [downloadFragment, setDownloadFragment] = useState<IDownloadFragment>({ url: '', name: '', animation: false });

  const downloadFragmentHandler = (event: React.MouseEvent<object>) => {
    if (!(event.target instanceof HTMLSpanElement)) {
      return;
    }

    const downloadLink = String(event.target.dataset.downloadLink);
    const downloadName = String(event.target.textContent);

    if (downloadFragment.animation) {
      setDownloadFragment((prevState: IDownloadFragment) => ({ ...prevState, animation: false }));
    }

    setDownloadFragment((prevState: IDownloadFragment) => ({ ...prevState, url: downloadLink }));
    setDownloadFragment((prevState: IDownloadFragment) => ({ ...prevState, name: downloadName }));
    setTimeout(() => setDownloadFragment((prevState: IDownloadFragment) => ({ ...prevState, animation: true })), 300);
  };

  return (
    <Layout title={SEO_TITLE} description={SEO_DESCRIPTION} canonicalHref={SEO_CANONICAL} cover={SEO_COVER} menuGenres={menuGenres} menuNew={menuNew}>
      <BookBreadcrumbs bookTitle={book.book.title} bookId={book.id} authorFullName={authorFullName} authorId={book.author.id} genres={book.genres} />
      <BookInfo book={book} reviewsCount={reviews.total_count} formRef={refReviewForm} />
      <TabMenu bookId={book.id} bookType={book.type} reviewsCount={reviews.total_count} />
      <div className={styles.bookDescription}>
        <p className={styles.bookDescription__txt}>{book.book.description}</p>
      </div>
      {!!book.files.length && (
        <div className={styles.availableFormats}>
          <h2 className={styles.h2}>Скачать бесплатно книгу «{book.book.title}»</h2>

          {downloadFragment.url.length > 0 && (
            <div className={styles.availableFormats__block}>
              <div className={styles.availableFormats__title}>Фрагмент в формате {downloadFragment.name}</div>
              <div className={styles.availableFormats__progressBarWrap}>
                <span className={downloadFragment.animation ? `${styles.availableFormats__progressBar} ${styles.availableFormats__progressBar_active}` : `${styles.availableFormats__progressBar}`}></span>
              </div>
              <a
                href={`${downloadFragment.url}?lfrom=1176614134`}
                target="_blank"
                className={downloadFragment.animation ? `btnSmall ${styles.availableFormats__btn} ${styles.availableFormats__btn_active}` : `btnSmall ${styles.availableFormats__btn}`}
              >
                Cкачать {downloadFragment.name}
              </a>
            </div>
          )}

          <div className={styles.availableFormats__list}>
            {book.files.map(
              (file: IBookFiles) =>
                file.type !== 'mobi.prc' &&
                file.type !== 'ios.epub' && (
                  <span
                    onClick={downloadFragmentHandler}
                    data-download-link={`${DEV_ABSOLUTE_URL}/trial/${file.type === 'mp3' ? 'mp3' : `${file.type === 'pdf' ? 'pdf' : 'txt'}`}/${file.external_file_id}.${file.type}`}
                    key={`${file.type}`}
                    className={`${styles.availableFormats__item} ${styles.ref}`}
                  >
                    {file.type}
                  </span>
                )
            )}
          </div>
        </div>
      )}
      {bookData.type === 'book' && !book.hide_litres_data && (
        <div className={styles.readOnline}>
          <h2 className={styles.h2}>Читать онлайн «{book.book.title}»</h2>
          <LitresReader externalId={bookData.external_id} />
        </div>
      )}
      {reviews.total_count > 0 && (
        <>
          <div className={styles.book__title}>
            <h2 className={styles.h2}>Отзывы</h2>
          </div>
          <ReviewsList reviews={reviews} />
        </>
      )}
      <div ref={refReviewForm} id="bookReviewForm">
        <FormReview bookId={book.id} bookAlias={book.alias} />
      </div>

      {/* Цитаты */}
      {/* <div className={styles.book__title}>
        <h2 className={styles.h2}>Цитаты</h2>
      </div>
      <ScrollCardsList>
        <div className={styles.quoteItem}>
          Я дочь богатых родителей и невеста канцлера при императоре. А ещё я ангел. Что я делаю в военной академии? Прячусь от жениха, учусь контролирую свою огненную магию и время от времени убегаю от неприятностей. Но они меня догнали.
        </div>
        <div className={styles.quoteItem}>
          Я дочь богатых родителей и невеста канцлера при императоре. А ещё я ангел. Что я делаю в военной академии? Прячусь от жениха, учусь контролирую свою огненную магию и время от времени убегаю от неприятностей. Но они меня догнали.
        </div>
        <div className={styles.quoteItem}>
          Я дочь богатых родителей и невеста канцлера при императоре. А ещё я ангел. Что я делаю в военной академии? Прячусь от жениха, учусь контролирую свою огненную магию и время от времени убегаю от неприятностей. Но они меня догнали.
        </div>
        <div className={styles.quoteItem}>
          Я дочь богатых родителей и невеста канцлера при императоре. А ещё я ангел. Что я делаю в военной академии? Прячусь от жениха, учусь контролирую свою огненную магию и время от времени убегаю от неприятностей. Но они меня догнали.
        </div>
        <div className={styles.quoteItem}>
          Я дочь богатых родителей и невеста канцлера при императоре. А ещё я ангел. Что я делаю в военной академии? Прячусь от жениха, учусь контролирую свою огненную магию и время от времени убегаю от неприятностей. Но они меня догнали.
        </div>
        <div className={styles.quoteItem}>
          Я дочь богатых родителей и невеста канцлера при императоре. А ещё я ангел. Что я делаю в военной академии? Прячусь от жениха, учусь контролирую свою огненную магию и время от времени убегаю от неприятностей. Но они меня догнали.
        </div>
      </ScrollCardsList> */}

      {otherAuthorBooks.length > 0 && <SimilarBooks books={otherAuthorBooks} authorName={authorFullName} authorId={book.author.id} />}
    </Layout>
  );
};

export default BookDetail;
